import {
  PVP_BOMB_BLAST_RADIUS_MU,
  PVP_BOMB_KNOCKBACK_EDGE_BPS,
  PVP_BOMB_KNOCKBACK_MU,
  PVP_BOMB_KNOCKBACK_TICKS,
} from './balance';
import {
  divTrunc,
  type IntVector,
  isqrt,
  normalise,
  scaleDirection,
  VECTOR_SCALE,
} from './vectors';

/**
 * **O empurrão do Núcleo Atômico, e só ele.**
 *
 * ## Porque isto vive no duelo e não em `damage/`
 *
 * Porque o §7 do gate é explícito: na caça **nada** é empurrado, e a ausência
 * tem de ser uma regra da simulação em vez de uma compensação do renderer. Um
 * empurrão em `damage/` seria código partilhado que a caça teria de se lembrar
 * de não chamar, e "lembrar-se de não chamar" é a forma como um mob acaba a
 * deslizar. Aqui, a caça não tem por onde lhe tocar: o módulo não é importado
 * por `hunt/`, e o compilador diz isso melhor do que um comentário.
 *
 * ## Deslocamento, e nunca velocidade
 *
 * O casco não ganha inércia. O empurrão é uma distância TOTAL - decidida no
 * instante da explosão - repartida pelos tiques da duração, e entregue ao mesmo
 * `pvpAdvanceShip` que o impulsionador já usa, no parâmetro `drift`. É isso que
 * faz o §6 fechar sem nenhuma regra nova:
 *
 * - **paredes e obstáculos travam**, porque `drift` é aplicado ANTES do
 *   empurra-para-fora e do recorte da arena, exatamente como um passo do
 *   jogador;
 * - **velocidade, runas e joias de movimento não alteram o deslocamento**,
 *   porque `speedBps` só multiplica o passo do JOGADOR, e o jogador está
 *   bloqueado;
 * - **o input é ignorado**, porque o chamador passa `moveX = moveY = 0` enquanto
 *   houver tiques.
 *
 * ## Bater numa parede não devolve o controlo
 *
 * A contagem de tiques desce sempre, tenha o casco andado ou não. Um empurrão
 * que terminasse ao encostar seria um empurrão que se cancela encostando, e
 * encostar é a coisa mais fácil de fazer numa arena com cinco pilastras.
 */

/**
 * O empurrão que um casco está a sofrer, ou `null`.
 *
 * Quatro inteiros e mais nada. Estado que decide o FUTURO - onde a nave vai
 * estar e se ela obedece ao teclado - portanto atravessa a instantânea inteiro,
 * e uma sala reaberta retoma-o onde estava em vez de o reiniciar.
 *
 * `dirX`/`dirY` são uma direção normalizada à escala {@link VECTOR_SCALE}, como
 * toda a direção neste duelo: nenhum ângulo, nenhum seno, nenhum float.
 */
export interface PvpKnockbackState {
  readonly dirX: number;
  readonly dirY: number;
  /** Unidades de mundo por percorrer. Reparte-se pelos tiques que faltam. */
  remainingMu: number;
  /** Tiques por cumprir, incluindo o próximo. Zero é o mesmo que `null`. */
  ticks: number;
}

/**
 * A força a esta distância do centro, em basis points da força no centro.
 *
 *     bps = borda + (10 000 - borda) x (raio - distância) / raio
 *
 * Linear entre `10 000` no centro e {@link PVP_BOMB_KNOCKBACK_EDGE_BPS} à borda,
 * com uma única divisão inteira. Fora do raio devolve zero - um casco fora da
 * explosão não é empurrado, e não é preciso um segundo teste para o dizer.
 */
export function pvpKnockbackFalloffBps(distanceMu: number, radiusMu: number): number {
  if (radiusMu <= 0) {
    throw new RangeError(`a blast radius is positive, received ${radiusMu}`);
  }
  if (distanceMu < 0) {
    throw new RangeError(`a distance is not negative, received ${distanceMu}`);
  }
  if (distanceMu >= radiusMu) {
    return 0;
  }
  const span = 10_000 - PVP_BOMB_KNOCKBACK_EDGE_BPS;
  return PVP_BOMB_KNOCKBACK_EDGE_BPS + divTrunc(span * (radiusMu - distanceMu), radiusMu);
}

/**
 * As oito direções de recurso, para um casco EXATAMENTE no centro.
 *
 * O contrato pede uma direção determinística derivada do id do evento quando
 * `normalize(posição - centro)` não existe, porque o vetor é nulo. Oito
 * direções construídas com a mesma `normalise` que tudo o resto usa: nenhuma
 * tabela de senos, nenhum ângulo, e valores idênticos em qualquer motor.
 *
 * Oito e não dezasseis porque isto é o desempate de uma coincidência exata ao
 * milésimo de unidade. O que importa é ser reprodutível e não ser sempre a
 * mesma, não ser fina.
 */
const CENTRE_FALLBACK_DIRECTIONS: readonly IntVector[] = Object.freeze(
  (
    [
      [1, 0],
      [1, 1],
      [0, 1],
      [-1, 1],
      [-1, 0],
      [-1, -1],
      [0, -1],
      [1, -1],
    ] as const
  ).map(([x, y]) => {
    const direction = normalise(x, y);
    if (direction === null) {
      throw new RangeError('a fallback knockback direction cannot be the zero vector');
    }
    return Object.freeze(direction);
  }),
);

/**
 * **A direção do empurrão: radialmente para fora do centro da explosão.**
 *
 * `normalize(posiçãoDaNave - centroDaExplosão)`, e nunca uma direção aleatória:
 * dois cascos em lados opostos da mesma explosão saem em sentidos opostos porque
 * é isso que a geometria diz, e não porque alguém sorteou.
 *
 * `entropy` e `seatIndex` só entram no caso degenerado - o casco exatamente no
 * centro - e entram os dois porque a alternativa é pior: derivar só da entropia
 * do evento mandaria dois cascos empilhados no mesmo pixel para o mesmo lado, e
 * eles ficariam empilhados.
 */
export function pvpKnockbackDirection(
  shipX: number,
  shipY: number,
  blastX: number,
  blastY: number,
  entropy: number,
  seatIndex: number,
): IntVector {
  const radial = normalise(shipX - blastX, shipY - blastY);
  if (radial !== null) {
    return radial;
  }
  const count = CENTRE_FALLBACK_DIRECTIONS.length;
  const index = ((((entropy >>> 0) + seatIndex) % count) + count) % count;
  return CENTRE_FALLBACK_DIRECTIONS[index] as IntVector;
}

/**
 * O empurrão que esta explosão impõe a este casco, ou `null` se não impõe nenhum.
 *
 * Devolve `null` fora do raio, e a decisão de IGNORAR um segundo empurrão sobre
 * um casco já empurrado **não é tomada aqui**: esta função responde "o que esta
 * explosão faria", e quem sabe o que o casco já está a sofrer é a simulação. Uma
 * função que soubesse as duas coisas seria uma função que a instantânea teria de
 * conseguir reconstruir.
 */
export function pvpKnockbackFrom(input: {
  readonly shipX: number;
  readonly shipY: number;
  readonly blastX: number;
  readonly blastY: number;
  readonly entropy: number;
  readonly seatIndex: number;
  /**
   * O raio da explosão que empurra, e a força dela (gate V28H.U).
   *
   * Ausentes valem os da Bomba Atômica, que é quem sempre chamou isto - a
   * assinatura antiga continua a dizer exactamente o que dizia. O Catolé passa
   * os seus: seis vezes o raio e três vezes a força, que são decisões aprovadas.
   *
   * Parametrizados em vez de duplicados numa segunda função: a queda com a
   * distância, a direcção desempatada e o reparto pelos tiques são a MESMA
   * física, e duas cópias dela seriam duas oportunidades de uma divergir.
   */
  readonly blastRadiusMu?: number;
  readonly strengthMu?: number;
}): PvpKnockbackState | null {
  const dx = input.shipX - input.blastX;
  const dy = input.shipY - input.blastY;
  const distance = isqrt(dx * dx + dy * dy);
  const falloffBps = pvpKnockbackFalloffBps(
    distance,
    input.blastRadiusMu ?? PVP_BOMB_BLAST_RADIUS_MU,
  );
  if (falloffBps <= 0) {
    return null;
  }
  const direction = pvpKnockbackDirection(
    input.shipX,
    input.shipY,
    input.blastX,
    input.blastY,
    input.entropy,
    input.seatIndex,
  );
  return {
    dirX: direction.x,
    dirY: direction.y,
    remainingMu: divTrunc((input.strengthMu ?? PVP_BOMB_KNOCKBACK_MU) * falloffBps, 10_000),
    ticks: PVP_BOMB_KNOCKBACK_TICKS,
  };
}

/**
 * O passo DESTE tique, e o estado que sobra depois dele.
 *
 * O que resta é repartido pelos tiques que faltam - `divTrunc(resto, tiques)` -
 * portanto o último tique leva a sobra e a distância total é gasta exatamente,
 * sem um resíduo que se perde e sem um passo maior no fim.
 *
 * Uma função pura que devolve os dois: a simulação aplica o passo e guarda o
 * estado, e não há nenhuma ordem entre as duas coisas que se possa trocar.
 */
export function pvpKnockbackStep(state: PvpKnockbackState): {
  readonly drift: IntVector;
  readonly next: PvpKnockbackState | null;
} {
  if (state.ticks <= 0) {
    return { drift: { x: 0, y: 0 }, next: null };
  }
  const stepMu = divTrunc(state.remainingMu, state.ticks);
  const drift = scaleDirection({ x: state.dirX, y: state.dirY }, stepMu);
  const ticks = state.ticks - 1;
  return {
    drift,
    next:
      ticks <= 0
        ? null
        : {
            dirX: state.dirX,
            dirY: state.dirY,
            remainingMu: state.remainingMu - stepMu,
            ticks,
          },
  };
}
